/* Resolving the language, and letting somebody change it — STORY-042.
 *
 * ── this is the rung that was never connected ───────────────────────────────
 * `I18nProvider` has always taken a `locale` prop, documented in its own source
 * as "Overrides the browser. Set from the signed-in User's stored preference".
 * `Providers` rendered it with no prop at all, so the browser won on every
 * screen for everyone — while the emails rendered in the recipient's stored
 * locale, correctly and tested. The two halves of the product disagreed, and
 * the disagreement read as a bug in the translations.
 *
 * It sits inside `QueryClientProvider` and outside `I18nProvider` because it
 * needs the one to answer and the other to be told.
 */

import type { Locale } from '@eventuley/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { CURRENT_USER_KEY, useCurrentUser } from '../features/auth/index.js';
import { request } from '../shared/api/client.js';
import { I18nProvider } from '../shared/i18n/index.js';
import {
  forgetLocale,
  rememberLocale,
  resolvePreferredLocale,
  storedLocale,
} from '../shared/i18n/preference.js';

interface LocaleControl {
  readonly locale: Locale;
  /** True while a signed-in User's choice is being written to their account. */
  readonly saving: boolean;
  /** Set when the write failed. The UI stays on the chosen language regardless. */
  readonly error: unknown;
  choose(locale: Locale): void;
}

const LocaleContext = createContext<LocaleControl | null>(null);

export function LocaleProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  const browser = typeof navigator === 'undefined' ? [] : navigator.languages;

  /* The choice is React state as well as a browser entry, and it has to be
   * both. Writing `localStorage` renders nothing, so a visitor who is not
   * signed in would pick a language and watch the screen not change until
   * something unrelated happened to re-render it. */
  const [chosen, setChosen] = useState<Locale | undefined>(storedLocale);

  /* The reader's own answer beats the account until the account has caught up,
   * which is the "adopted on first sign-in" criterion made true rather than
   * hoped for: without it, signing in would show the account's language for as
   * long as the adoption took to round-trip, and the reader would watch their
   * language flip back and then forward again. */
  const locale = useMemo(
    () => resolvePreferredLocale(chosen ?? user?.locale, browser),
    [chosen, user?.locale, browser],
  );

  const save = useMutation({
    mutationFn: (next: Locale) =>
      request('/account/locale', { method: 'PUT', body: { locale: next } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY }),
  });

  const saveLocale = save.mutate;
  const userId = user?.id;
  const accountLocale = user?.locale;

  /* One path to the account, taken whenever a signed-in reader's own answer and
   * their account disagree — whether they just chose it or chose it on the
   * sign-in screen a minute before they had an account to write to.
   *
   * The story asks for the second case explicitly, and the reason is that the
   * alternative is absurd: somebody sets the language ON the sign-in screen,
   * signs in, and the screen reverts to whatever their browser says — throwing
   * away an answer they gave thirty seconds earlier on the very page that asked
   * for it.
   *
   * Guarded on the two values actually differing, so one choice is one write,
   * and a failed write is not retried in a loop — it is retried the next time
   * the app loads, which is what the "not saved" warning promises. */
  useEffect(() => {
    if (userId === undefined || chosen === undefined) return;
    if (accountLocale === chosen) {
      /* The account holds the answer now, so the browser copy is a second place
       * for one answer to live and therefore a second place for it to
       * disagree. */
      forgetLocale();
      return;
    }
    saveLocale(chosen);
  }, [userId, accountLocale, chosen, saveLocale]);

  const control = useMemo<LocaleControl>(
    () => ({
      locale,
      saving: save.isPending,
      error: save.error,
      choose: (next) => {
        /* Written to the browser first, and unconditionally.
         *
         * The screen changes language either way, and if the save fails the
         * choice still survives a reload — a preference that appears to save
         * and does not is worse than a refusal, and the story asks that the UI
         * stay on the language the person picked rather than silently
         * reverting. */
        rememberLocale(next);
        setChosen(next);
      },
    }),
    [locale, save.isPending, save.error],
  );

  return (
    <LocaleContext.Provider value={control}>
      <I18nProvider locale={locale}>{children}</I18nProvider>
    </LocaleContext.Provider>
  );
}

export function useLocaleControl(): LocaleControl {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocaleControl must be used inside a LocaleProvider.');
  return value;
}
